import React from "react";
import RelativeTime from "components/RelativeTime";
import { Lock } from "lucide-react";
import { TICKET_STATUS_LABELS, TICKET_STATUS_CATEGORY } from "constants/tickets";

/**
 * ActivityTimeline (SOUND-493)
 * -------------------------------------------------------
 * Append-only `ticketEvents` as a compact vertical timeline. Category-colored
 * dot + one-line summary + RelativeTime. Answers "what happened" (the thread
 * answers "what was said"), so it stays glance-scannable and never competes
 * with the conversation. Non-internal viewers never receive internal
 * comment_added events (API-stripped).
 */

// status -> dot color class (waiting/declined override their category)
function dotForStatus(status) {
  if (status === "waiting") return "bg-warning";
  if (status === "declined") return "bg-destructive";
  switch (TICKET_STATUS_CATEGORY[status]) {
    case "in_progress":
      return "bg-info";
    case "done":
      return "bg-success";
    case "canceled":
      return "bg-muted-foreground";
    case "todo":
    default:
      return "bg-brand";
  }
}

function describe(event) {
  const { type, data = {} } = event;
  switch (type) {
    case "created":
      return {
        dot: "bg-brand",
        text: `Opened ${data.after?.kind || "ticket"}`,
      };
    case "status_changed": {
      const before = data.before?.status;
      const after = data.after?.status;
      return {
        dot: dotForStatus(after),
        text: `${TICKET_STATUS_LABELS[before] || before || "—"} → ${
          TICKET_STATUS_LABELS[after] || after || "—"
        }`,
        reason: data.reason,
      };
    }
    case "assigned": {
      const after = data.after;
      const name = after?.displayName || after?.email;
      return {
        dot: "bg-muted-foreground",
        text: name ? `Assigned to ${name}` : "Unassigned",
      };
    }
    case "comment_added": {
      const internal = data.visibility === "internal";
      return {
        dot: internal ? "bg-warning" : "bg-muted-foreground",
        text: internal ? "Internal note" : "Commented",
        internal,
      };
    }
    default:
      return { dot: "bg-muted-foreground", text: type };
  }
}

export default function ActivityTimeline({ events = [] }) {
  if (!events.length) {
    return (
      <p className="text-sm text-muted-foreground">No activity yet.</p>
    );
  }

  return (
    <div className="flex flex-col">
      {events.map((event, i) => {
        const { dot, text, reason, internal } = describe(event);
        const actor = event.actor?.displayName || event.actor?.email;
        const isLast = i === events.length - 1;

        return (
          <div key={event.eventId || i} className="grid grid-cols-[16px_1fr] gap-2.5">
            {/* rail */}
            <div className="relative flex justify-center">
              <span
                className={`mt-1 h-2 w-2 rounded-full ${dot} ring-2 ring-card`}
                style={{ zIndex: 1 }}
                aria-hidden="true"
              />
              {!isLast && (
                <span className="absolute left-1/2 top-2 -translate-x-1/2 border-l border-border" style={{ bottom: "-10px" }} />
              )}
            </div>
            {/* text */}
            <div className={isLast ? "" : "pb-3.5"}>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground">
                <span>{text}</span>
                {internal && (
                  <span className="inline-flex items-center gap-0.5 text-warning">
                    <Lock className="h-3 w-3" />
                  </span>
                )}
              </div>
              {reason && (
                <div className="mt-0.5 text-xs italic text-muted-foreground">
                  “{reason}”
                </div>
              )}
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {actor ? `${actor} · ` : ""}
                <RelativeTime value={event.createdAt} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
